import {
  Component,
  Injector,
  OnDestroy,
  OnInit,
  AfterViewInit,
  TemplateRef,
  Type,
  ViewChild,
  inject,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { CountriesCurrenciesService } from '@app/finxone-web-frontend/app/lib/services/countries-currencies-service/countries-currencies.service';
import { WorkflowFormsService } from '@app/finxone-web-frontend/app/lib/services/workflow-forms/workflow-forms.service';
import {
  buildHandleBarModel,
  filterIfHandlebarNotRendered,
} from '@app/finxone-web-frontend/app/lib/utils/template/model-builder.utils';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { IconCacheService } from '@finxone-platform/shared/services';
import {
  baseCssConfigStyle,
  BaseCssProperties,
  DatePickerTypes,
  NewUiZoneWidgetConfig,
  ProviderClass,
  UiZoneWidgetAttributeConfig,
  UiZoneWidgetConfig,
  WidgetNames,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import Handlebars from 'handlebars';
import { debounceTime, distinctUntilChanged, startWith, Subject, takeUntil } from 'rxjs';
import { createChildWidgetInjector } from '../../../zones/utils';
import { ZonePageUrlProvider } from '../../../zones/zone-url-provider';
import { AddressWidgetComponent } from '../form-widgets/address-widget/address-widget.component';
import { FormSubmitter } from '../form-widgets/form-submitter/form-submitter.component';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { FormActionState } from '@app/finxone-web-frontend/app/lib/state/form-submision.state';
import { getNestedValue } from '@app/finxone-web-frontend/app/lib/utils/nested-object-path.utils';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-address-history',
  templateUrl: './address-history.component.html',
})
export class AddressHistoryComponent extends FormSubmitter implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('moveInDateTemplate', { static: true })
  public moveInDateTemplate!: TemplateRef<{ index: number; entryForm: FormGroup }>;

  @AutoDestroy override destroy$: Subject<void> = new Subject<void>();
  public widgetConfig: UiZoneWidgetConfig;
  public formId: string;
  public parentFormPath: string;
  // When set, addressList nests under one entry (at whatever index is currently pinned via
  // WorkflowFormsService.getActiveFormArrayIndex) of a FormArray called arrayChildKey on formId's
  // page-scoped form, instead of directly on formId's page-scoped form itself. Lets this widget
  // be reused as "addresses for the director currently being added/edited" inside a wizard.
  public arrayChildKey?: string;
  public datePickerTypes = DatePickerTypes;
  public addressWidgetComponent: Type<AddressWidgetComponent> = AddressWidgetComponent;
  public moveInDateValidationConfig = [{ type: 'required', message: 'Date is a required field' }];
  public renderedPreviousAddressSubheaderText = '';
  private internalForm: FormGroup | null = null;
  private isAddingEntry = false;
  private addressWidgetInjectorMap = new Map<number, Injector>();
  private attributes: UiZoneWidgetAttributeConfig;
  public colorTextAlignStyle: baseCssConfigStyle;

  // Injecting services using the inject() function to avoid issues with the parent class FormSubmitter which also injects Store
  public dataIn = inject<ProviderClass<UiZoneWidgetAttributeConfig>>(ProviderClass);
  private workflowFormsService = inject(WorkflowFormsService);
  private zonePageUrlProvider = inject(ZonePageUrlProvider);
  private readonly injector = inject(Injector);
  private readonly countriesCurrenciesService = inject(CountriesCurrenciesService);
  private readonly iconCacheService = inject(IconCacheService);
  private readonly configService = inject(ConfigService);

  constructor() {
    super(inject(Store));
  }

  ngOnInit(): void {
    this.initializeWidgetConfig();
    this.setupFormStructure();
    this.setupValidators();
    this.initializeAddressEntries();
    this.setupValueChangesListener();
    this.initPreviousAddressSubheader();
    this.initializeStyles();
  }

  private initializeWidgetConfig(): void {
    if (this.dataIn) {
      this.widgetConfig = this.dataIn.widgetConfig;
      this.attributes = this.dataIn.attri as UiZoneWidgetAttributeConfig;
      this.formId = this.attributes?.widgetProperties?.['parentForm'];
      this.arrayChildKey = this.attributes?.widgetProperties?.['parentFormArrayKey'];
    }
  }

  private setupFormStructure(): void {
    // If no parentForm is configured, create an internal form
    if (!this.formId) {
      this.internalForm = new FormGroup({
        addressList: new FormArray([]),
      });
      return;
    }

    this.parentFormPath = this.arrayChildKey
      ? this.workflowFormsService.resolveArrayItemFormPath(
          this.formId,
          this.arrayChildKey,
          this.zonePageUrlProvider,
        ).path
      : this.workflowFormsService.generateCustomFormId(this.formId, this.zonePageUrlProvider);

    const parentForm = this.workflowFormsService.getFormPart(this.parentFormPath);
    if (!parentForm) {
      console.warn(
        `AddressHistoryComponent: Parent form not found at path: ${this.parentFormPath}. Using internal form.`,
      );
      // Fallback to internal form
      this.internalForm = new FormGroup({
        addressList: new FormArray([]),
      });
    } else if (!parentForm.get('addressList')) {
      parentForm.addControl('addressList', new FormArray([]));
    }
  }

  private setupValidators(): void {
    this.addressHistoryArray.setValidators(this.addressHistoryCoverageValidator());
  }

  /**
   * Reads the saved addressList for this widget's context. When arrayChildKey is set, that means
   * addressList lives nested inside one entry of a FormArray (e.g. businessOnboardingFlow.directors[i].addressList)
   * rather than directly on formId's own state (businessOnboardingFlow.addressList).
   */
  private getSavedAddressList(): Record<string, unknown>[] | undefined {
    if (!this.formId) return undefined;
    const stateValues = this.store.selectSnapshot(FormActionState.getFormActionStateWithId(this.formId));

    if (this.arrayChildKey) {
      const index = this.workflowFormsService.getActiveFormArrayIndex(`${this.formId}.${this.arrayChildKey}`);
      if (index === null) return undefined;
      const entries = getNestedValue<Record<string, unknown>[]>(stateValues?.formData, this.arrayChildKey);
      return entries?.[index]?.['addressList'] as Record<string, unknown>[] | undefined;
    }

    return stateValues?.formData?.['addressList'];
  }

  private initializeAddressEntries(): void {
    if (this.formId) {
      const savedAddressList = this.getSavedAddressList();

      if (Array.isArray(savedAddressList) && savedAddressList.length > 0) {
        savedAddressList.forEach(() => this.addAddressEntry());
        savedAddressList.forEach((entryData: Record<string, unknown>, index: number) => {
          const entryForm = this.addressHistoryArray.at(index) as FormGroup;
          entryForm.patchValue(
            {
              moveInDate: entryData['moveInDate'] ?? '',
              day: entryData['day'] ?? '',
              month: entryData['month'] ?? '',
              year: entryData['year'] ?? '',
            },
            { emitEvent: false },
          );
        });
        return;
      }
    }

    if (this.addressHistoryArray.length === 0) {
      this.addAddressEntry();
    } else {
      // Sync existing entries
      for (let i = 0; i < this.addressHistoryArray.length; i++) {
        this.syncDateControls(i);
      }
    }
  }

  private setupValueChangesListener(): void {
    this.addressHistoryArray.valueChanges
      .pipe(
        startWith(this.addressHistoryArray.value),
        debounceTime(100),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.ensureCoverageEntries();
        this.addressHistoryArray.updateValueAndValidity({ emitEvent: false });
      });
  }

  private initPreviousAddressSubheader(): void {
    const templateStr = (this.attributes?.widgetProperties?.['previousAddressSubheaderText'] as string) ?? '';
    if (!templateStr) {
      return;
    }
    const compiled = Handlebars.compile(templateStr);
    buildHandleBarModel(this.store, templateStr, this.countriesCurrenciesService, this.iconCacheService)
      .pipe(takeUntil(this.destroy$))
      .subscribe((model) => {
        this.renderedPreviousAddressSubheaderText = filterIfHandlebarNotRendered(compiled(model));
      });
  }

  private initializeStyles(): void {
    this.colorTextAlignStyle = {
      ...this.configService.getSpecificBaseCssStyle(this.attributes, [
        BaseCssProperties.TEXT_BOLD,
        BaseCssProperties.TEXT_ITALIC,
        BaseCssProperties.TEXT_UNDERLINE,
        BaseCssProperties.FONT_SIZE,
        BaseCssProperties.TEXT_FONT_STYLE,
        BaseCssProperties.TEXTALIGN,
        BaseCssProperties.COLOR,
      ]),
    };
  }

  ngAfterViewInit(): void {
    this.patchSavedAddressData();
  }

  private patchSavedAddressData(): void {
    if (!this.formId) return;
    const savedAddressList = this.getSavedAddressList();

    if (!Array.isArray(savedAddressList) || savedAddressList.length === 0) return;

    savedAddressList.forEach((entryData: Record<string, unknown>, index: number) => {
      if (index >= this.addressHistoryArray.length) return;

      const entryForm = this.addressHistoryArray.at(index) as FormGroup;
      const addressSubForm = entryForm.get('address') as FormGroup;
      const addressData = entryData['address'] as Record<string, unknown>;

      // Only patch if the address widget has already added its controls to the sub-form
      if (addressData && addressSubForm && Object.keys(addressSubForm.controls).length > 0) {
        addressSubForm.patchValue(addressData, { emitEvent: false });
      }
    });
  }

  ngOnDestroy(): void {
    this.addressWidgetInjectorMap.clear();
  }

  public get addressHistoryArray(): FormArray<FormGroup> {
    // Try to get from parent form first
    if (this.parentFormPath) {
      const parentForm = this.workflowFormsService.getFormPart(this.parentFormPath);
      const addressList = parentForm?.get('addressList') as FormArray<FormGroup>;
      if (addressList) {
        return addressList;
      }
    }

    // Fall back to internal form
    if (this.internalForm) {
      return this.internalForm.get('addressList') as FormArray<FormGroup>;
    }

    // Return empty FormArray to prevent null/undefined errors in dependent code
    return new FormArray<FormGroup>([]);
  }

  public get subHeadingLabel(): string {
    return this.attributes?.widgetProperties?.['subHeadingLabel'] || '';
  }

  public get moveInDateLabel(): string {
    return this.attributes?.widgetProperties?.['moveInDateLabel'];
  }

  public get selectedDatePickerType(): DatePickerTypes {
    const configuredType = this.attributes?.widgetProperties?.['datePickerType'];
    if (configuredType === DatePickerTypes.INPUT) {
      return DatePickerTypes.INPUT;
    }
    return DatePickerTypes.DATEPICKER;
  }

  public get minAddressHistoryYears(): number {
    const years = Number(this.attributes?.widgetProperties?.['minAddressHistoryYears'] ?? 3);
    return Number.isFinite(years) && years > 0 ? years : 3;
  }

  public get coverageErrorMessage(): string {
    const template = this.attributes?.widgetProperties?.['coverageErrorMessage'] as string;
    return template?.replace('{minAddressHistoryYears}', String(this.minAddressHistoryYears)) || '';
  }

  public getAddressEntryControl(index: number): FormGroup {
    return this.addressHistoryArray.at(index) as FormGroup;
  }

  public getAddressWidgetInjector(index: number): Injector {
    const existing = this.addressWidgetInjectorMap.get(index);
    if (existing) {
      return existing;
    }

    const addressParentFormPath = this.getAddressFormPath(index);
    const widgetAttributes = {
      ...(this.widgetConfig?.attributes ?? {}),
      widgetProperties: {
        ...(this.widgetConfig?.attributes?.widgetProperties ?? {}),
        parentForm: addressParentFormPath,
        isAddressHistoryEntry: true,
      },
    };

    const addressWidgetConfig = {
      ...(this.widgetConfig as NewUiZoneWidgetConfig),
      uniqueId: `${this.dataIn?.uniqueId ?? 'address-history'}-address-${index}`,
      name: WidgetNames.FORM_ADDRESS_WIDGET,
      attributes: widgetAttributes,
    } as NewUiZoneWidgetConfig;

    const injector = createChildWidgetInjector(addressWidgetConfig, this.injector, this.dataIn);
    this.addressWidgetInjectorMap.set(index, injector);
    return injector;
  }

  public getAddressWidgetInputs(index: number): Record<string, unknown> {
    return {
      addressHistoryExtraTemplate: this.moveInDateTemplate,
      addressHistoryExtraContext: {
        index,
        entryForm: this.getAddressEntryControl(index),
      },
    };
  }

  public getMoveInDateControl(index: number): FormControl {
    return this.getAddressEntryControl(index).get('moveInDate') as FormControl;
  }

  public getDayControl(index: number): FormControl {
    return this.getAddressEntryControl(index).get('day') as FormControl;
  }

  public getMonthControl(index: number): FormControl {
    return this.getAddressEntryControl(index).get('month') as FormControl;
  }

  public getYearControl(index: number): FormControl {
    return this.getAddressEntryControl(index).get('year') as FormControl;
  }

  public isMoveInDateInvalid(index: number): boolean {
    const control = this.getMoveInDateControl(index);
    return !!(control.invalid && (control.dirty || control.touched));
  }

  public shouldShowCoverageError(): boolean {
    return !!(
      this.addressHistoryArray.errors?.['addressHistoryIncomplete'] &&
      (this.addressHistoryArray.dirty || this.addressHistoryArray.touched)
    );
  }

  private addAddressEntry(): void {
    // Set flag to prevent concurrent additions
    this.isAddingEntry = true;

    const index = this.addressHistoryArray.length;
    const entryForm = new FormGroup({
      moveInDate: new FormControl('', [Validators.required]),
      day: new FormControl(''),
      month: new FormControl(''),
      year: new FormControl(''),
      // Pre-create as FormGroup so AddressWidgetComponent can populate it directly,
      // avoiding the double customForm-page_X nesting that a FormControl snapshot caused.
      address: new FormGroup({}),
    });

    this.addressHistoryArray.push(entryForm, { emitEvent: false });
    this.syncDateControls(index);

    // Reset flag immediately after setup
    this.isAddingEntry = false;
  }

  private syncDateControls(index: number): void {
    const entry = this.getAddressEntryControl(index);
    const moveInDateControl = entry.get('moveInDate');
    const dayControl = entry.get('day');
    const monthControl = entry.get('month');
    const yearControl = entry.get('year');

    if (!moveInDateControl || !dayControl || !monthControl || !yearControl) {
      return;
    }

    // When moveInDate changes, update day/month/year
    moveInDateControl.valueChanges.pipe(takeUntil(this.destroy$), debounceTime(300)).subscribe((value) => {
      if (value) {
        const date = this.parseDate(value);
        if (date) {
          dayControl.setValue(date.getDate().toString(), { emitEvent: false });
          // Month is 0-based in Date (0 = January), and our month control also uses 0-based values
          monthControl.setValue(date.getMonth().toString(), { emitEvent: false });
          yearControl.setValue(date.getFullYear().toString(), { emitEvent: false });
        }
      }
    });

    // When day/month/year change, update moveInDate
    const updateMoveInDate = () => {
      const day = dayControl.value;
      const month = monthControl.value;
      const year = yearControl.value;

      if (day && month != null && year) {
        // Only construct date if year has 4 digits to prevent partial year values like "20" being interpreted as year 20 CE
        if (year.toString().length === 4) {
          // Month value is already 0-based (0 = January, 11 = December), so no need to subtract 1
          const date = new Date(parseInt(year), parseInt(month), parseInt(day));
          if (!Number.isNaN(date.getTime())) {
            // Changed to emit event so the form array detects the change
            moveInDateControl.setValue(date);
          }
        }
      }
    };

    dayControl.valueChanges.pipe(takeUntil(this.destroy$), debounceTime(300)).subscribe(updateMoveInDate);
    monthControl.valueChanges.pipe(takeUntil(this.destroy$), debounceTime(300)).subscribe(updateMoveInDate);
    yearControl.valueChanges.pipe(takeUntil(this.destroy$), debounceTime(300)).subscribe(updateMoveInDate);

    // Initialize from existing moveInDate value if present
    if (moveInDateControl.value) {
      const date = this.parseDate(moveInDateControl.value);
      if (date) {
        dayControl.setValue(date.getDate().toString(), { emitEvent: false });
        // Month is 0-based in Date (0 = January), and our month control also uses 0-based values
        monthControl.setValue(date.getMonth().toString(), { emitEvent: false });
        yearControl.setValue(date.getFullYear().toString(), { emitEvent: false });
      }
    }
  }

  private ensureCoverageEntries(): void {
    // Prevent concurrent additions
    if (this.isAddingEntry) {
      return;
    }

    if (this.addressHistoryArray.length === 0) {
      this.addAddressEntry();
      return;
    }

    // Get all entries that have valid move-in dates
    const entriesWithDates = this.addressHistoryArray.controls.filter((group) => {
      return !!group.get('moveInDate')?.value;
    });

    // If there are no entries with dates yet, do nothing
    if (entriesWithDates.length === 0) {
      return;
    }

    // Calculate if we need more address history
    const oldestMoveInDate = this.getOldestMoveInDate();
    if (!oldestMoveInDate) {
      return;
    }

    // Calculate threshold using months for precision (36 months = 3 years)
    const thresholdDate = new Date();
    const requiredMonths = this.minAddressHistoryYears * 12;
    thresholdDate.setMonth(thresholdDate.getMonth() - requiredMonths);

    // If the oldest move-in date is more recent than the threshold,
    // we need more address history
    if (oldestMoveInDate > thresholdDate) {
      // Check if the last entry is empty (no date entered yet)
      const lastEntry = this.addressHistoryArray.at(this.addressHistoryArray.length - 1);
      const lastEntryHasDate = !!lastEntry.get('moveInDate')?.value;

      // Only add a new entry if the last entry already has a date
      if (lastEntryHasDate) {
        this.addAddressEntry();
      }
    } else {
      this.removeRedundantEntries(thresholdDate);
    }
  }

  private removeRedundantEntries(thresholdDate: Date): void {
    // Coverage is sufficient - remove trailing entries that are no longer needed.
    // An entry is redundant if coverage remains sufficient without it.
    while (this.addressHistoryArray.length > 1) {
      const lastIndex = this.addressHistoryArray.length - 1;

      const datesWithoutLast = this.addressHistoryArray.controls
        .slice(0, lastIndex)
        .map((group) => this.parseDate(group.get('moveInDate')?.value))
        .filter((value): value is Date => !!value);

      if (datesWithoutLast.length === 0) {
        break;
      }

      const oldestWithoutLast = datesWithoutLast.reduce((oldest, current) =>
        current < oldest ? current : oldest,
      );

      if (oldestWithoutLast <= thresholdDate) {
        // Still sufficient without the last entry — remove it
        this.addressHistoryArray.removeAt(lastIndex);
        this.addressWidgetInjectorMap.delete(lastIndex);
      } else {
        break;
      }
    }
  }

  private getOldestMoveInDate(): Date | null {
    const parsedDates = this.addressHistoryArray.controls
      .map((group) => this.parseDate(group.get('moveInDate')?.value))
      .filter((value): value is Date => !!value);

    if (!parsedDates.length) {
      return null;
    }

    return parsedDates.reduce((oldest, current) => {
      return current < oldest ? current : oldest;
    });
  }

  private parseDate(value: unknown): Date | null {
    if (!value) {
      return null;
    }

    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return value;
    }

    const parsedDate = new Date(value as string);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  private getAddressFormPath(index: number): string {
    if (!this.formId) {
      return `address-history-${this.dataIn?.uniqueId ?? 'default'}.addressList.${index}`;
    }
    // parentFormPath is the generateCustomFormId result (e.g. 'onboarding.customForm-myPage').
    // addressList lives under parentFormPath, not under the raw formId, so we must use
    // parentFormPath as the base to build a path that getFormPart() can actually resolve.
    return `${this.parentFormPath ?? this.formId}.addressList.${index}`;
  }

  /**
   * Custom validator to ensure that the address history covers the required number of years.
   * Validates that:
   * - There is at least one address entry.
   * - All entries have a valid move-in date.
   * - The oldest move-in date is at least `minAddressHistoryYears` years (36 months) in the past.
   *
   */
  private addressHistoryCoverageValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!(control instanceof FormArray) || control.length === 0) {
        return { addressHistoryIncomplete: true };
      }

      const dates = control.controls
        .map((group) => this.parseDate(group.get('moveInDate')?.value))
        .filter((value): value is Date => !!value);

      if (dates.length !== control.length) {
        return { addressHistoryIncomplete: true };
      }

      // Calculate threshold using months for precision (36 months = 3 years)
      const thresholdDate = new Date();
      const requiredMonths = this.minAddressHistoryYears * 12;
      thresholdDate.setMonth(thresholdDate.getMonth() - requiredMonths);

      const oldestDate = dates.reduce((oldest, current) => {
        return current < oldest ? current : oldest;
      });

      if (oldestDate > thresholdDate) {
        return { addressHistoryIncomplete: true };
      }

      return null;
    };
  }
}
